import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import cx from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMedia } from "react-use";

import { getChainName } from "config/chains";
import { getIcon } from "config/icons";
import { APP_NAME } from "config/ui";
import {
  useLeaderboardChainId,
  useLeaderboardDataTypeState,
  useLeaderboardIsCompetition,
  useLeaderboardPageKey,
  useLeaderboardPositions,
  useLeaderboardRankedAccounts,
  useLeaderboardTimeframeTypeState,
  useLeaderboardTiming,
} from "context/SyntheticsStateContext/hooks/leaderboardHooks";
import { selectLeaderboardIsLoading } from "context/SyntheticsStateContext/selectors/leaderboardSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { CompetitionType } from "domain/synthetics/leaderboard";
import { LEADERBOARD_PAGES } from "domain/synthetics/leaderboard/constants";
import { useChainId } from "lib/chains";
import { mustNeverExist } from "lib/types";
import { switchNetwork } from "lib/wallets";
import useWallet from "lib/wallets/useWallet";

import { Dropdown } from "components/Dropdown/Dropdown";
import ExternalLink from "components/ExternalLink/ExternalLink";
import PageTitle from "components/PageTitle/PageTitle";
import { SwipeTabs } from "components/SwipeTabs/SwipeTabs";
import Tabs from "components/Tabs/Tabs";

import { CompetitionCountdown } from "./CompetitionCountdown";
import { CompetitionPrizes } from "./CompetitionPrizes";
import { LeaderboardAccountsTable } from "./LeaderboardAccountsTable";
import { LeaderboardNavigation } from "./LeaderboardNavigation";
import { LeaderboardPositionsTable } from "./LeaderboardPositionsTable";

const competitionsTabs = [0, 1];
const leaderboardTimeframeTabs = [0, 1, 2, 3, 4];
const leaderboardTimeframeTypes = ["24h", "7days", "14days", "30days", "all"] as const;
const leaderboardDataTypeTabs = [0, 1];

export function LeaderboardContainer() {
  useLingui();
  const isCompetition = useLeaderboardIsCompetition();
  const showDataTypeTabs = false;

  const [activeLeaderboardTimeframeIndex, setActiveLeaderboardTimeframeIndex] = useState(0);
  const [activeLeaderboardDataTypeIndex, setActiveLeaderboardDataTypeIndex] = useState(0);
  const [activeCompetitionIndex, setActiveCompetitionIndex] = useState(0);

  const leaderboardPageKey = useLeaderboardPageKey();

  const { chainId } = useChainId();
  const { active } = useWallet();

  const page = LEADERBOARD_PAGES[leaderboardPageKey];

  const [, setLeaderboardTimeframeType] = useLeaderboardTimeframeTypeState();
  const [leaderboardDataType, setLeaderboardDataType] = useLeaderboardDataTypeState();

  const competitionLabels = [t`Top PnL ($)`, t`Top PnL (%)`];
  const leaderboardTimeframeLabels = [t`24H`, t`7D`, t`14D`, t`30D`, t`ALL`];
  const leaderboardDataTypeLabels = [t`Top Addresses`, t`Top Positions`];

  const activeCompetition: CompetitionType | undefined = isCompetition
    ? activeCompetitionIndex === 0
      ? "notionalPnl"
      : "pnlPercentage"
    : undefined;

  const handleLeaderboardTimeframeTabChange = useCallback(
    (index: number) => setActiveLeaderboardTimeframeIndex(index),
    [setActiveLeaderboardTimeframeIndex]
  );
  const handleCompetitionTabChange = useCallback(
    (index: number) => setActiveCompetitionIndex(index),
    [setActiveCompetitionIndex]
  );

  const handleLeaderboardDataTypeTabChange = useCallback(
    (index: number) => setActiveLeaderboardDataTypeIndex(index),
    []
  );

  const pageKey = useLeaderboardPageKey();
  const leaderboardChainId = useLeaderboardChainId();

  useEffect(() => {
    setActiveLeaderboardTimeframeIndex(0);
    setActiveCompetitionIndex(0);
  }, [pageKey]);

  useEffect(() => {
    const nextType = leaderboardTimeframeTypes[activeLeaderboardTimeframeIndex] ?? "all";
    setLeaderboardTimeframeType(nextType);
  }, [activeLeaderboardTimeframeIndex, setLeaderboardTimeframeType]);

  useEffect(() => {
    if (activeLeaderboardDataTypeIndex === 0) {
      setLeaderboardDataType("accounts");
    } else {
      setLeaderboardDataType("positions");
    }
  }, [activeLeaderboardDataTypeIndex, setLeaderboardDataType]);

  const title = useMemo(() => {
    switch (leaderboardPageKey) {
      case "leaderboard":
        return t`Leaderboard`;

      case "march_13-20_2024":
        return t`EIP-4844 Competition`;

      case "march_20-27_2024":
        return t`EIP-4844 Competition`;

      default:
        throw mustNeverExist(leaderboardPageKey);
    }
  }, [leaderboardPageKey]);

  const handleSwitchNetworkClick = useCallback(() => {
    switchNetwork(leaderboardChainId, active);
  }, [active, leaderboardChainId]);

  const wrongNetworkSwitcher = useMemo(() => {
    if (leaderboardPageKey === "leaderboard") return null;
    if (chainId === leaderboardChainId) return null;
    if (!page.isCompetition) return null;

    return (
      <div className="Leaderboard__another-network">
        <Trans>
          This competition is held on the {getChainName(page.chainId)} network.{" "}
          <span className="link-underline" onClick={handleSwitchNetworkClick}>
            Change your network
          </span>{" "}
          to participate.
        </Trans>
      </div>
    );
  }, [chainId, handleSwitchNetworkClick, leaderboardChainId, leaderboardPageKey, page]);

  const isMobile = useMedia("(max-width: 1000px)");

  const description = useMemo(() => {
    switch (leaderboardPageKey) {
      case "leaderboard":
        return t`Leaderboard for traders on ${APP_NAME}.`;
      case "march_13-20_2024":
      case "march_20-27_2024":
        return (
          <>
            Powered by the Arbitrum DAO STIP.&nbsp;
            <ExternalLink href="https://open.substack.com/pub/gmxio/p/the-gmx-eip4844-trading-competition">
              <Trans>Read the rules</Trans>
            </ExternalLink>
            .{wrongNetworkSwitcher}{" "}
            {isMobile && (
              <>
                <CompetitionCountdown size="mobile" />
              </>
            )}
          </>
        );

      default:
        throw mustNeverExist(leaderboardPageKey);
    }
  }, [isMobile, leaderboardPageKey, wrongNetworkSwitcher]);

  const leaderboardDataTypeTabsOptions = useMemo(() => {
    return leaderboardDataTypeTabs.map((value) => ({
      value,
      label: leaderboardDataTypeLabels[value],
    }));
  }, [leaderboardDataTypeLabels]);

  const leaderboardTimeframeTabsOptions = useMemo(() => {
    return leaderboardTimeframeTabs.map((value) => ({
      value,
      label: leaderboardTimeframeLabels[value],
    }));
  }, [leaderboardTimeframeLabels]);

  const competitionsTabsOptions = useMemo(() => {
    return competitionsTabs.map((value) => ({
      value,
      label: competitionLabels[value],
    }));
  }, [competitionLabels]);

  return (
    <div className="GlobalLeaderboards">
      {/* <LeaderboardNavigation /> */}
      <div className="Leaderboard-Title default-container">
        {/* <div>
          <h1 className="text-34 font-bold" data-qa="leaderboard-page">
            {title} <img alt={t`Chain Icon`} src={getIcon(page.isCompetition ? page.chainId : chainId, "network")} />
          </h1>
          <div className="Page-description">{description}</div>
        </div> */}
        <PageTitle
          title={title}
          subtitle={description}
          showNetworkIcon={false}
          isTop
          qa="leaderboard-page"
          hasBackButton
          subContent={
            !isCompetition && showDataTypeTabs ? (
              <>
                {/* <Tabs
              selectedValue={activeLeaderboardDataTypeIndex}
              onChange={handleLeaderboardDataTypeTabChange}
              options={leaderboardDataTypeTabsOptions}
            /> */}
                <SwipeTabs
                  options={leaderboardDataTypeTabs}
                  optionLabels={leaderboardDataTypeLabels}
                  option={activeCompetitionIndex}
                  onChange={handleLeaderboardDataTypeTabChange}
                  optionClassname="!px-24"
                  wrapperClassname="w-full sm:w-auto"
                  qa="leaderboard-data-type"
                />
              </>
            ) : undefined
          }
        />
      </div>

      {/* {!isCompetition && (
        <Tabs
          selectedValue={activeLeaderboardTimeframeIndex}
          onChange={handleLeaderboardTimeframeTabChange}
          type="inline"
          options={leaderboardTimeframeTabsOptions}
          className={cx("LeaderboardContainer__leaderboard-tabs default-container", {
            "LeaderboardContainer__leaderboard-tabs_positions": leaderboardDataType === "positions",
          })}
        />
      )} */}
      {/* {isCompetition && (
        <>
          <div className="LeaderboardContainer__competition-tabs default-container">
            <Tabs
              selectedValue={activeCompetitionIndex}
              onChange={handleCompetitionTabChange}
              options={competitionsTabsOptions}
            />
            {!isMobile && <CompetitionCountdown className="default-container" size="desktop" />}
          </div>
          <br />
          <br />
        </>
      )} */}
      {/* {isCompetition && activeCompetition && (
        <CompetitionPrizes leaderboardPageKey={leaderboardPageKey} competitionType={activeCompetition} />
      )} */}

      <Table
        activeCompetition={activeCompetition}
        timeFrameTabs={
          !isCompetition && (
            <>
              <Tabs
                selectedValue={activeLeaderboardTimeframeIndex}
                onChange={handleLeaderboardTimeframeTabChange}
                type="inline"
                options={leaderboardTimeframeTabsOptions}
                variant="filled"
                size="small"
                regularOptionClassname="!min-w-52 !px-12"
                className="!hidden !gap-4 sm:!flex"
                // className={cx("LeaderboardContainer__leaderboard-tabs default-container", {
                //   "LeaderboardContainer__leaderboard-tabs_positions": leaderboardDataType === "positions",
                // })}
              />
              <Dropdown
                options={leaderboardTimeframeTabsOptions}
                onSelect={(option) => {
                  const index = leaderboardTimeframeTabsOptions.findIndex((o) => o.value === option.value);
                  if (index !== -1) {
                    handleLeaderboardTimeframeTabChange(index);
                  }
                }}
                selectedOption={leaderboardTimeframeTabsOptions[activeLeaderboardTimeframeIndex]}
                className="!min-w-40 sm:!hidden"
              />
            </>
          )
        }
      />
    </div>
  );
}

function Table({
  activeCompetition,
  timeFrameTabs,
}: {
  activeCompetition: CompetitionType | undefined;
  timeFrameTabs: React.ReactNode;
}) {
  const { isStartInFuture } = useLeaderboardTiming();
  const leaderboardPageKey = useLeaderboardPageKey();
  const leaderboardDataType = useLeaderboardDataTypeState()[0];
  if (isStartInFuture) return null;

  const table =
    leaderboardPageKey === "leaderboard" && leaderboardDataType === "positions" ? (
      <PositionsTable />
    ) : (
      <AccountsTable activeCompetition={activeCompetition} timeFrameTabs={timeFrameTabs} />
    );

  return <div className="default-container">{table}</div>;
}

function AccountsTable({
  activeCompetition,
  timeFrameTabs,
}: {
  activeCompetition: CompetitionType | undefined;
  timeFrameTabs: React.ReactNode;
}) {
  const accounts = useLeaderboardRankedAccounts();
  const isLoading = useSelector(selectLeaderboardIsLoading);
  const accountsStruct = useMemo(
    () => ({
      isLoading,
      data: accounts ? accounts : [],
      error: null,
      updatedAt: 0,
    }),
    [accounts, isLoading]
  );

  return (
    <LeaderboardAccountsTable
      activeCompetition={activeCompetition}
      accounts={accountsStruct}
      timeFrameTabs={timeFrameTabs}
    />
  );
}

function PositionsTable() {
  const positions = useLeaderboardPositions();
  const isLoading = useSelector(selectLeaderboardIsLoading);
  const positionsStruct = useMemo(
    () => ({
      isLoading,
      data: positions ? positions : [],
      error: null,
      updatedAt: 0,
    }),
    [positions, isLoading]
  );
  return <LeaderboardPositionsTable positions={positionsStruct} />;
}
